import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API_CONFIG, getApiUrl, getAuthHeaders } from '../config/api';
import { useThemedStyles } from '../theme/useThemedStyles';
import { formatDateTime } from '../utils/dateFormat';
import CalendarSharingPanel from '../components/auth/CalendarSharingPanel';

type Role = 'Admin' | 'Doctor' | 'Secretary';
type Status = 'Pending' | 'Approved' | 'Rejected';

interface User {
  id: number;
  username: string;
  fullName: string;
  email: string;
  role: Role;
  status: Status;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  doctorId: number | null;
  googleLinked?: boolean;
  isInitialAdmin?: boolean;
  canViewPatients: boolean;
  canEditPatients: boolean;
  canDeletePatients: boolean;
  canViewAppointments: boolean;
  canEditAppointments: boolean;
  canDeleteAppointments: boolean;
  canViewPayments: boolean;
  canEditPayments: boolean;
  canViewReports: boolean;
  canManageUsers: boolean;
  canManageDoctors: boolean;
  canManageSettings: boolean;
  canViewWhatsApp: boolean;
  canSendWhatsApp: boolean;
  canManageWhatsApp: boolean;
}

interface UserManagementProps {
  token: string;
}

interface DoctorOption {
  id: number;
  firstName?: string;
  lastName?: string;
  secondLastName?: string;
}

interface Approval { role: Role; doctorId: number | null }

const MAX_ADMINS = 2;
const ROLES: Role[] = ['Secretary', 'Doctor', 'Admin'];
const btnStyle: React.CSSProperties = { margin: '0 4px', padding: '8px 16px', borderRadius: 4, border: 'none', cursor: 'pointer', background: '#1976d2', color: 'white' };
const statusColors: Record<Status, string> = { Pending: '#d97706', Approved: '#16a34a', Rejected: '#6b7280' };

const doctorLabel = (d: DoctorOption) => `${d.firstName || ''} ${d.lastName || ''} ${d.secondLastName || ''}`.trim() || `Doctor #${d.id}`;

export default function UserManagement({ token }: UserManagementProps) {
  const { t } = useTranslation();
  const theme = useThemedStyles();
  const [users, setUsers] = useState<User[]>([]);
  const [doctors, setDoctors] = useState<DoctorOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [approvals, setApprovals] = useState<Record<number, Approval>>({});
  const [reapprovingId, setReapprovingId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | Status>('all');

  useEffect(() => {
    fetchUsers();
    fetchDoctors();
    // Initial load on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const url = getApiUrl(API_CONFIG.ENDPOINTS.USERS || '/users');
      const response = await fetch(url, { headers: getAuthHeaders(token) });
      if (!response.ok) throw new Error('requestFailed');
      const data = await response.json();
      setUsers(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'requestFailed');
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctors = async () => {
    try {
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.DOCTORS), { headers: getAuthHeaders(token) });
      if (!response.ok) return;
      const data = await response.json();
      setDoctors(Array.isArray(data) ? data : []);
    } catch {
      // Non-blocking: user management still works without doctor list
    }
  };

  // Runs a user mutation, surfacing the API's stable error code, then reloads the list.
  const perform = async (path: string, method: string, body?: object) => {
    setBusy(true); setError('');
    try {
      const response = await fetch(getApiUrl(path), { method, headers: getAuthHeaders(token), ...(body ? { body: JSON.stringify(body) } : {}) });
      const data = response.status === 204 ? null : await response.json().catch(() => null);
      if (!response.ok) throw new Error(data?.error || 'requestFailed');
      await fetchUsers();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'requestFailed');
      return false;
    } finally { setBusy(false); }
  };

  const updateBody = (user: User, overrides: Partial<User> = {}) => {
    const next = { ...user, ...overrides };
    return {
      fullName: next.fullName, email: next.email, role: next.role, isActive: next.isActive, doctorId: next.doctorId,
      canViewPatients: next.canViewPatients, canEditPatients: next.canEditPatients, canDeletePatients: next.canDeletePatients,
      canViewAppointments: next.canViewAppointments, canEditAppointments: next.canEditAppointments, canDeleteAppointments: next.canDeleteAppointments,
      canViewPayments: next.canViewPayments, canEditPayments: next.canEditPayments, canViewReports: next.canViewReports,
      canManageUsers: next.canManageUsers, canManageDoctors: next.canManageDoctors, canManageSettings: next.canManageSettings,
      canViewWhatsApp: next.canViewWhatsApp, canSendWhatsApp: next.canSendWhatsApp, canManageWhatsApp: next.canManageWhatsApp
    };
  };

  const approval = (id: number): Approval => approvals[id] ?? { role: 'Secretary', doctorId: null };
  const setApproval = (id: number, value: Partial<Approval>) => setApprovals(prev => ({ ...prev, [id]: { ...approval(id), ...value } }));

  const approveUser = async (user: User) => {
    if (await perform(`/users/${user.id}/approve`, 'POST', approval(user.id))) setReapprovingId(null);
  };

  const rejectUser = (user: User) => {
    if (!window.confirm(t('staff.confirmReject', { name: user.fullName || user.email }))) return;
    perform(`/users/${user.id}/reject`, 'POST');
  };

  const deleteUser = (user: User) => {
    if (!window.confirm(t('users.confirmDelete'))) return;
    perform(`/users/${user.id}`, 'DELETE');
  };

  const saveRole = async () => {
    if (!editingUser) return;
    if (await perform(`/users/${editingUser.id}`, 'PUT', updateBody(editingUser))) setEditingUser(null);
  };

  const themedBtnStyle: React.CSSProperties = { ...btnStyle, ...theme.button };
  const smallBtn: React.CSSProperties = { ...themedBtnStyle, padding: '4px 8px', fontSize: 12, marginBottom: 4 };
  const dangerBtn: React.CSSProperties = { ...smallBtn, background: '#dc2626', borderColor: '#dc2626' };
  const approveBtn: React.CSSProperties = { ...smallBtn, background: '#16a34a', borderColor: '#16a34a' };
  const selectStyle: React.CSSProperties = { ...theme.input, padding: 6, borderRadius: 4 };
  const cell: React.CSSProperties = { padding: '10px', borderBottom: `1px solid ${theme.border}` };

  if (loading && users.length === 0) return <div style={{ color: theme.text }}>{t('patientList.loading')}</div>;

  const pending = users.filter(u => u.status === 'Pending');
  const activeAdmins = users.filter(u => u.role === 'Admin' && u.status === 'Approved' && u.isActive).length;
  const listed = users.filter(u => statusFilter === 'all' ? u.status !== 'Pending' : u.status === statusFilter);

  const approvalControls = (user: User) => {
    const value = approval(user.id);
    return <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      <select aria-label={t('staff.role')} value={value.role} onChange={e => setApproval(user.id, { role: e.target.value as Role })} style={selectStyle}>
        {ROLES.map(role => <option key={role} value={role} disabled={role === 'Admin' && activeAdmins >= MAX_ADMINS}>{t(`staff.roles.${role}`)}</option>)}
      </select>
      {value.role === 'Doctor' && <select aria-label={t('staff.doctor')} required value={value.doctorId ?? ''} onChange={e => setApproval(user.id, { doctorId: e.target.value ? Number(e.target.value) : null })} style={selectStyle}>
        <option value="">{t('staff.selectDoctor')}</option>
        {doctors.map(d => <option key={d.id} value={d.id}>{doctorLabel(d)}</option>)}
      </select>}
      <button style={approveBtn} disabled={busy || (value.role === 'Doctor' && !value.doctorId)} onClick={() => approveUser(user)}>{t('staff.approve')}</button>
    </span>;
  };

  return (
    <div style={{ padding: '20px', color: theme.text }}>
      <h2>{t('users.title', 'User Management')}</h2>
      {error && <div role="alert" style={{ color: '#dc2626', marginBottom: '10px' }}>{t(`staff.errors.${error}`, { defaultValue: t('staff.errors.requestFailed') })}</div>}

      <section style={{ ...theme.card, padding: 16, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>{t('staff.pendingTitle')} ({pending.length})</h3>
        {pending.length === 0 ? <p>{t('staff.pendingEmpty')}</p> : (
          <table className="phone-card-table" style={{ width: '100%', borderCollapse: 'collapse', color: theme.text }}>
            <thead>
              <tr style={{ background: theme.surface }}>
                <th style={{ ...cell, textAlign: 'left' }}>{t('users.name')}</th>
                <th style={{ ...cell, textAlign: 'left' }}>{t('users.email')}</th>
                <th style={{ ...cell, textAlign: 'left' }}>{t('staff.requestedAt')}</th>
                <th style={{ ...cell, textAlign: 'left' }}>{t('users.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {pending.map(user => (
                <tr key={user.id} data-testid={`pending-user-${user.id}`}>
                  <td data-label={t('users.name')} style={cell}>{user.fullName}</td>
                  <td data-label={t('users.email')} style={cell}>{user.email}</td>
                  <td data-label={t('staff.requestedAt')} style={{ ...cell, fontSize: 12 }}>{formatDateTime(user.createdAt, '')}</td>
                  <td data-label={t('users.actions')} style={cell}>
                    {approvalControls(user)}{' '}
                    <button style={dangerBtn} disabled={busy} onClick={() => rejectUser(user)}>{t('staff.reject')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {editingUser && <form onSubmit={e => { e.preventDefault(); saveRole(); }} style={{ ...theme.card, padding: 16, marginBottom: 16 }}>
        <h3>{t('staff.editRole')}: {editingUser.fullName}</h3>
        <label>{t('staff.role')}<select value={editingUser.role} disabled={editingUser.isInitialAdmin} onChange={e => setEditingUser({ ...editingUser, role: e.target.value as Role })}>
          {ROLES.map(role => <option key={role} value={role}>{t(`staff.roles.${role}`)}</option>)}
        </select></label>{' '}
        <label>{t('staff.doctor')}<select required={editingUser.role === 'Doctor'} value={editingUser.doctorId ?? ''} onChange={e => setEditingUser({ ...editingUser, doctorId: e.target.value ? Number(e.target.value) : null })}>
          <option value="">{t('staff.selectDoctor')}</option>{doctors.map(d => <option key={d.id} value={d.id}>{doctorLabel(d)}</option>)}
        </select></label>{' '}
        <fieldset style={{ marginTop: 12 }}><legend>{t('whatsapp.adminSettings', 'WhatsApp access')}</legend>
          <label><input type="checkbox" checked={editingUser.canViewWhatsApp} onChange={e => setEditingUser({ ...editingUser, canViewWhatsApp: e.target.checked })} /> {t('whatsapp.viewAccess', 'View inbox')}</label>{' '}
          <label><input type="checkbox" checked={editingUser.canSendWhatsApp} onChange={e => setEditingUser({ ...editingUser, canSendWhatsApp: e.target.checked })} /> {t('whatsapp.sendAccess', 'Send messages')}</label>{' '}
          <label><input type="checkbox" checked={editingUser.canManageWhatsApp} onChange={e => setEditingUser({ ...editingUser, canManageWhatsApp: e.target.checked })} /> {t('whatsapp.manageAccess', 'Manage connection')}</label>
        </fieldset>{' '}
        <button disabled={busy}>{t('staff.save')}</button>{' '}<button type="button" onClick={() => setEditingUser(null)}>{t('staff.cancel')}</button>
      </form>}

      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <label>{t('staff.statusFilter')}{' '}
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as 'all' | Status)} style={selectStyle}>
            {(['all', 'Approved', 'Rejected', 'Pending'] as const).map(s => <option key={s} value={s}>{t(`staff.userStatus.${s}`)}</option>)}
          </select>
        </label>
        <span style={{ fontSize: 14 }}>{t('staff.adminCount', { count: activeAdmins, max: MAX_ADMINS })}</span>
      </div>

      <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, overflow: 'hidden' }}>
        <table className="phone-card-table" style={{ width: '100%', borderCollapse: 'collapse', color: theme.text }}>
          <thead>
            <tr style={{ background: theme.surface }}>
              <th style={{ ...cell, textAlign: 'left' }}>{t('users.name')}</th>
              <th style={{ ...cell, textAlign: 'left' }}>{t('users.email')}</th>
              <th style={{ ...cell, textAlign: 'left' }}>{t('users.role')}</th>
              <th style={{ ...cell, textAlign: 'left' }}>{t('users.status')}</th>
              <th style={{ ...cell, textAlign: 'center' }}>{t('users.active')}</th>
              <th style={{ ...cell, textAlign: 'left' }}>{t('users.lastLogin')}</th>
              <th style={{ ...cell, textAlign: 'center' }}>{t('users.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {listed.map((user, i) => (
              <tr key={user.id} style={{ background: i % 2 === 0 ? theme.inputBg : theme.surface }}>
                <td data-label={t('users.name')} style={cell}>
                  {user.fullName}
                  {user.isInitialAdmin && <div style={{ fontSize: 11, opacity: 0.75 }}>{t('staff.initialAdmin')}</div>}
                </td>
                <td data-label={t('users.email')} style={cell}>{user.email}</td>
                <td data-label={t('users.role')} style={cell}>
                  <span style={{ background: user.role === 'Admin' ? '#dc2626' : user.role === 'Doctor' ? '#2563eb' : '#10b981', color: '#fff', padding: '4px 8px', borderRadius: 4, fontSize: 12 }}>
                    {t(`staff.roles.${user.role}`)}
                  </span>
                </td>
                <td data-label={t('users.status')} style={cell}>
                  <span style={{ background: statusColors[user.status], color: '#fff', padding: '4px 8px', borderRadius: 4, fontSize: 12 }}>
                    {t(`staff.userStatus.${user.status}`)}
                  </span>
                </td>
                <td data-label={t('users.active')} style={{ ...cell, textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={user.isActive}
                    disabled={busy || user.status !== 'Approved' || user.isInitialAdmin}
                    onChange={() => perform(`/users/${user.id}`, 'PUT', updateBody(user, { isActive: !user.isActive }))}
                    style={{ cursor: 'pointer' }}
                  />
                </td>
                <td data-label={t('users.lastLogin')} style={{ ...cell, fontSize: 12 }}>
                  {formatDateTime(user.lastLoginAt, t('users.never'))}
                </td>
                <td data-label={t('users.actions')} style={{ ...cell, textAlign: 'center' }}>
                  {user.status === 'Approved' && <button style={smallBtn} disabled={busy} onClick={() => setEditingUser({ ...user })}>{t('staff.editRole')}</button>}
                  {user.status === 'Approved' && !user.isInitialAdmin && <button style={dangerBtn} disabled={busy} onClick={() => rejectUser(user)}>{t('staff.revokeAccess')}</button>}
                  {user.status === 'Rejected' && (reapprovingId === user.id
                    ? <>{approvalControls(user)}{' '}<button style={smallBtn} onClick={() => setReapprovingId(null)}>{t('staff.cancel')}</button></>
                    : <button style={approveBtn} disabled={busy} onClick={() => setReapprovingId(user.id)}>{t('staff.reapprove')}</button>)}
                  {user.status === 'Pending' && <>{approvalControls(user)}{' '}<button style={dangerBtn} disabled={busy} onClick={() => rejectUser(user)}>{t('staff.reject')}</button></>}
                  {!user.isInitialAdmin && <button style={dangerBtn} disabled={busy} onClick={() => deleteUser(user)}>{t('users.delete')}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CalendarSharingPanel token={token} />
    </div>
  );
}
